var __jsx = React.createElement;
import React, { useMemo } from "react";
import classNames from "classnames";
import Button from "../../common/Button";
import Tooltip from "../../common/Tooltip";
import AwesomeIcon from "../../AwesomeIcon";
import { faCircleXmark, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useStoreState } from "pullstate";
import ACRStore from "../../../utils/ACRStore";
import { SUBMISSION_STATUS } from "../../../constants/acr";
import { useSharedFormData } from "./SharedFormDataContext";
var SubmissionFormFooter = function SubmissionFormFooter(_ref) {
  var workspaceId = _ref.workspaceId,
    populateMutation = _ref.populateMutation,
    hasFormRendered = _ref.hasFormRendered,
    showPopulateOverlay = _ref.showPopulateOverlay,
    isSaving = _ref.isSaving,
    isSubmitting = _ref.isSubmitting,
    onSave = _ref.onSave,
    onSubmit = _ref.onSubmit,
    onPopulate = _ref.onPopulate;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // Get shared data from context
  var _useSharedFormData = useSharedFormData(),
    submissionDetail = _useSharedFormData.submissionDetail,
    errorsForSubmit = _useSharedFormData.errorsForSubmit,
    allRequiredFieldsCompleted = _useSharedFormData.allRequiredFieldsCompleted,
    changedFieldsSet = _useSharedFormData.changedFieldsSet;

  // Check if user is logged in to ACR for this workspace
  var isACRLoggedIn = useStoreState(ACRStore, function (s) {
    var _s$workspaceConfigs$w, _s$workspaceConfigs$w2;
    return (_s$workspaceConfigs$w = (_s$workspaceConfigs$w2 = s.workspaceConfigs[workspaceId]) === null || _s$workspaceConfigs$w2 === void 0 ? void 0 : _s$workspaceConfigs$w2.is_logged_in) !== null && _s$workspaceConfigs$w !== void 0 ? _s$workspaceConfigs$w : false;
  }, [workspaceId]);

  // Check if submission is in a read-only state (submitted or accepted)
  var isSubmissionReadOnly = useMemo(function () {
    var _submissionDetail$sta;
    var status = submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$sta = submissionDetail.status) === null || _submissionDetail$sta === void 0 ? void 0 : _submissionDetail$sta.toLowerCase();
    return status === SUBMISSION_STATUS.SUBMITTED || status === SUBMISSION_STATUS.ACCEPTED;
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.status]);

  // Determine if Populate button should be shown
  var showPopulateButton = useMemo(function () {
    var _submissionDetail$sta2;
    var status = submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$sta2 = submissionDetail.status) === null || _submissionDetail$sta2 === void 0 ? void 0 : _submissionDetail$sta2.toLowerCase();

    // Show populate button for statuses where editing is allowed
    var isEditableStatus = status === SUBMISSION_STATUS.PENDING || status === SUBMISSION_STATUS.REJECTED || status === SUBMISSION_STATUS.READY || status === SUBMISSION_STATUS.VALIDATION_FAILED || status === SUBMISSION_STATUS.ERROR || status === SUBMISSION_STATUS.FAILED;

    // Don't show button if all data is already populated
    var isAllDataPopulated = (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.is_data_populated) && (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.is_emr_fields_populated);
    return submissionDetail && !isAllDataPopulated && isEditableStatus && !(populateMutation !== null && populateMutation !== void 0 && populateMutation.isLoading);
  }, [submissionDetail, populateMutation === null || populateMutation === void 0 ? void 0 : populateMutation.isLoading]);

  // Generate tooltip content for populate button
  var populateTooltipText = useMemo(function () {
    if (!submissionDetail) return null;
    var isDataMissing = !submissionDetail.is_data_populated;
    var isEmrMissing = !submissionDetail.is_emr_fields_populated;
    var messageKey = "";
    // If both are missing, show combined message
    if (isDataMissing && isEmrMissing) {
      messageKey = "label.acr.populate_tooltip_both";
    }
    // If only one is missing, show specific message
    else if (isDataMissing) {
      messageKey = "label.acr.populate_tooltip_data";
    } else if (isEmrMissing) {
      messageKey = "label.acr.populate_tooltip_emr";
    }
    if (!messageKey) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "flex items-start gap-2"
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      name: faInfoCircle,
      className: "w-4 h-4 flex-shrink-0 mt-0.5"
    }), /*#__PURE__*/React.createElement("span", null, t(messageKey)));
  }, [submissionDetail, t]);

  // Save button enables when fields are changed (regardless of errors)
  var hasDirtyFields = useMemo(function () {
    return changedFieldsSet && changedFieldsSet.size > 0;
  }, [changedFieldsSet]);

  // Check blocking errors for Submit
  // (excludes required markers, optional warnings, and stale backend errors)
  var hasBlockingErrorsInAllFields = useMemo(function () {
    return errorsForSubmit.length > 0;
  }, [errorsForSubmit]);
  var populateLoading = (populateMutation === null || populateMutation === void 0 ? void 0 : populateMutation.isLoading) || false;
  return /*#__PURE__*/React.createElement("div", {
    className: "bg-[#F3F4F4] dark:bg-[#0C1723] border-t border-[#BBBFC2] dark:border-[#646B72] p-4 flex flex-col gap-3 flex-shrink-0"
  }, !isACRLoggedIn && /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-100 border border-amber-300 text-amber-800 dark:bg-amber-900/50 dark:border dark:border-amber-800/40 dark:text-amber-400 text-sm"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faCircleXmark,
    className: "w-4 h-4 shrink-0"
  }), /*#__PURE__*/React.createElement("span", null, t("label.acr.login_validation_text"))), /*#__PURE__*/React.createElement("div", {
    className: "flex gap-3 justify-between items-center"
  }, showPopulateButton && /*#__PURE__*/React.createElement(Tooltip, {
    tooltipText: populateTooltipText,
    position: "left",
    verticalPosition: "top",
    className: "[&_.tooltip-trigger]:m-0"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onPopulate,
    disabled: populateLoading || showPopulateOverlay,
    className: classNames("flex gap-3 items-center px-4 py-3 rounded-6 text-[#CF4A32] dark:text-[#FF9B8C] border border-[#FF786973] hover:text-gray-800 dark:hover:text-gray-100 text-md font-semibold", {
      "cursor-progress": populateLoading
    }),
    style: {
      background: "linear-gradient(135deg, rgba(255, 120, 105, 0.2), rgba(152, 174, 217, 0.12))"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: "/assets/icons/aira-logo.svg",
    alt: "AI filled",
    className: "h-5 w-5 flex-shrink-0 object-contain"
  }), populateLoading ? t("button.populating") : t("button.populate"))), /*#__PURE__*/React.createElement("div", {
    className: "flex gap-3 ml-auto"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onSave,
    className: "h-[44px] cursor-pointer rounded-xl px-6 text-base text-[#0C1723] dark:text-[#F6F7F7] bg-[#0082801F] dark:bg-[#00D0CD1F] hover:bg-[#00828029] hover:dark:bg-[#00D0CD33] duration-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0082801F] disabled:hover:dark:bg-[#00D0CD1F]",
    disabled: !hasDirtyFields || isSubmissionReadOnly || hasFormRendered && showPopulateOverlay || isSaving
  }, isSaving ? t("button.saving") : t("button.save_as_draft")), /*#__PURE__*/React.createElement(Button, {
    isPrimary: true,
    onClick: onSubmit,
    className: "h-[44px] !rounded-xl px-6",
    disabled: !allRequiredFieldsCompleted || hasBlockingErrorsInAllFields || isSubmissionReadOnly || !isACRLoggedIn || hasFormRendered && showPopulateOverlay,
    isLoading: isSubmitting
  }, isSubmitting ? t("button.submitting") : t("button.submit_to_acr")))));
};
export default SubmissionFormFooter;